import { reactive, ref, watch, onBeforeUnmount } from 'vue'

/**
 * Liste paginée, triable, filtrable.
 *
 * - `fetcher(params, { signal })` renvoie { data, links, meta } ;
 * - `filters` devient `filter[clé]=valeur` (un tableau = « ou ») ;
 * - `extra` regroupe les paramètres de premier niveau (from, to, vehicle_id…) ;
 * - toute modification remet la page à 1 ; un filtre inconnu du serveur est ignoré.
 */
export function useList(fetcher, { filters = {}, extra = {}, sort = '', perPage = 25, search = true } = {}) {
  const state = reactive({
    items: [],
    meta: null,
    loading: false,
    error: null,
    page: 1,
    perPage,
    search: '',
    sort,
    filters: { ...filters },
    extra: { ...extra }
  })
  const lastLoadedAt = ref(0)
  let controller = null
  let debounce = null
  let seq = 0

  function params() {
    return {
      ...state.extra,
      page: state.page,
      per_page: state.perPage,
      search: search ? state.search.trim() : undefined,
      sort: state.sort || undefined,
      filter: state.filters
    }
  }

  async function load() {
    if (controller) controller.abort()
    controller = new AbortController()
    const mine = ++seq
    state.loading = true
    state.error = null
    try {
      const res = await fetcher(params(), { signal: controller.signal })
      if (mine !== seq) return
      state.items = res.data
      state.meta = res.meta
      lastLoadedAt.value = Date.now()
      // Dernière page supprimée : revenir sur une page existante.
      if (state.items.length === 0 && state.page > 1 && res.meta?.last_page && state.page > res.meta.last_page) {
        state.page = res.meta.last_page
      }
    } catch (e) {
      if (e.name === 'AbortError' || mine !== seq) return
      state.error = e
    } finally {
      if (mine === seq) state.loading = false
    }
  }

  function reset() {
    state.page = 1
    load()
  }

  watch(() => state.page, load)
  watch(() => [state.perPage, state.sort, JSON.stringify(state.filters), JSON.stringify(state.extra)], reset)
  watch(
    () => state.search,
    () => {
      clearTimeout(debounce)
      debounce = setTimeout(reset, 300)
    }
  )

  onBeforeUnmount(() => {
    clearTimeout(debounce)
    if (controller) controller.abort()
  })

  function toggleSort(key) {
    state.sort = state.sort === key ? `-${key}` : state.sort === `-${key}` ? '' : key
  }

  return { state, load, reset, toggleSort, lastLoadedAt }
}
